import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./AddCgt.css";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";

const API_BASE = "https://payyug.in/api";

const toInputDate = (value = new Date()) => {
  const d = new Date(value);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getActorId = () => {
  const superAdminId = Number(localStorage.getItem("adminuserId")) || 0;
  const rawUser = localStorage.getItem("user");

  if (!rawUser) return superAdminId;

  try {
    const parsed = JSON.parse(rawUser);
    return Number(parsed?.id || parsed?.userId || superAdminId) || superAdminId;
  } catch {
    return superAdminId;
  }
};

const AddCgt = () => {
  const superAdminId = Number(localStorage.getItem("adminuserId")) || 0;
  const actorId = getActorId();
  const navigate=useNavigate()

  const [branches, setBranches] = useState([]);
  const [centers, setCenters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    CGTID: "",
    Description: "",
    CGT_StartDate: toInputDate(),
    BranchId: "",
    CenterId: "",
    groupId: "",
    employeId: "",
  });

  const [editItem, setEditItem] = useState(null);
  const [editData, setEditData] = useState({
    Description: "",
    CGT_StartDate: toInputDate(),
    employeId: "",
  });

  const canSubmit =
    formData.CGTID.trim() &&
    formData.Description.trim() &&
    formData.CGT_StartDate &&
    formData.BranchId &&
    formData.CenterId &&
    formData.groupId;

  const fetchCgtList = async () => {
    if (!superAdminId) return;

    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/cgt/list`, {
        params: { superAdminId },
      });
      setList(res?.data?.data || res?.data || []);
    } catch (err) {
      console.error("Failed to fetch CGT list", err);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await axios.get("https://payyug.in/api/get-branch", {
          params: {
            SuperAdminId: superAdminId,
            limit: "all",
          },
        });
        setBranches(res?.data?.branches || []);
      } catch (err) {
        console.error("Failed to load branches", err);
      }
    };

    fetchBranches();
    fetchCgtList();
  }, [superAdminId]);

  useEffect(() => {
    if (!formData.BranchId) {
      setCenters([]);
      setGroups([]);
      setEmployees([]);
      return;
    }

    const fetchCenters = async () => {
      try {
        const res = await axios.get("https://payyug.in/api/group/centre", {
          params: {
            superAdminId,
            branchid: formData.BranchId,
            limit: 100000,
          },
        });
        setCenters(res?.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch centers", err);
        setCenters([]);
      }
    };

    fetchCenters();
    setFormData((prev) => ({
      ...prev,
      CenterId: "",
      groupId: "",
      employeId: "",
    }));
    setGroups([]);
    setEmployees([]);
  }, [formData.BranchId, superAdminId]);

  useEffect(() => {
    if (!formData.CenterId) {
      setGroups([]);
      setEmployees([]);
      return;
    }

    const fetchGroups = async () => {
      try {
        const res = await axios.get("https://payyug.in/api/groupupdate/getall", {
          params: {
            superAdminId,
            CenterId: formData.CenterId,
            limit: 100000,
          },
        });
        setGroups(res?.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch groups", err);
        setGroups([]);
      }
    };

    fetchGroups();
    setFormData((prev) => ({ ...prev, groupId: "", employeId: "" }));
    setEmployees([]);
  }, [formData.CenterId, superAdminId]);

  useEffect(() => {
    if (!formData.groupId) {
      setEmployees([]);
      return;
    }

    const fetchEmployees = async () => {
      try {
        const res = await axios.get("https://payyug.in/api/group-loan/group/list", {
          params: {
            superAdminId,
            branchId: formData.BranchId,
            centerId: formData.CenterId,
            groupId: formData.groupId,
            page: 1,
            limit: 1000,
          },
        });
        setEmployees(res?.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch employees", err);
        setEmployees([]);
      }
    };

    fetchEmployees();
    setFormData((prev) => ({ ...prev, employeId: "" }));
  }, [formData.groupId, formData.BranchId, formData.CenterId, superAdminId]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;

    const payload = {
      CGTID: formData.CGTID.trim(),
      Description: formData.Description.trim(),
      CGT_StartDate: formData.CGT_StartDate,
      BranchId: Number(formData.BranchId),
      CenterId: Number(formData.CenterId),
      groupId: Number(formData.groupId),
      superAdminId,
      createdBy: actorId,
    };

    if (formData.employeId) {
      payload.employeId = Number(formData.employeId);
    }

    try {
      setSubmitting(true);
      await axios.post(`${API_BASE}/cgt/create`, payload);
      await fetchCgtList();
      setFormData({
        CGTID: "",
        Description: "",
        CGT_StartDate: toInputDate(),
        BranchId: "",
        CenterId: "",
        groupId: "",
        employeId: "",
      });
      setCenters([]);
      setGroups([]);
      setEmployees([]);
      alert("CGT added successfully.");
    } catch (err) {
      console.error("Create CGT failed", err);
      alert(err?.response?.data?.message || "Failed to add CGT.");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    if (!id) return;
    const ok = window.confirm("Delete this CGT record?");
    if (!ok) return;

    try {
      await axios.delete(`${API_BASE}/cgt/delete/${id}`);
      await fetchCgtList();
      alert("CGT deleted successfully.");
    } catch (err) {
      console.error("Delete CGT failed", err);
      alert(err?.response?.data?.message || "Failed to delete CGT.");
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditData({
      Description: item?.Description || "",
      CGT_StartDate: item?.CGT_StartDate ? toInputDate(item.CGT_StartDate) : toInputDate(),
      employeId: item?.employeId ? String(item.employeId) : "",
    });
  };

  const closeEdit = () => {
    setEditItem(null);
    setEditData({ Description: "", CGT_StartDate: toInputDate(), employeId: "" });
  };

  const onUpdate = async (e) => {
    e.preventDefault();
    if (!editItem?.id) return;

    const payload = {
      Description: editData.Description.trim(),
      CGT_StartDate: editData.CGT_StartDate,
      updateBy: actorId,
    };

    if (editData.employeId) {
      payload.employeId = Number(editData.employeId);
    }

    try {
      await axios.put(`${API_BASE}/cgt/update/${editItem.id}`, payload);
      await fetchCgtList();
      closeEdit();
      alert("CGT updated successfully.");
    } catch (err) {
      console.error("Update CGT failed", err);
      alert(err?.response?.data?.message || "Failed to update CGT.");
    }
  };

  const rows = useMemo(() => (Array.isArray(list) ? list : []), [list]);

  return (
    <div className="cgt-crud-page">
      {/* Back wrapper */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 16px",
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          marginBottom: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <ArrowLeftIcon
          onClick={() => navigate(-1)}
          style={{
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        />

        <h2
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: "600",
            color: "#333",
          }}
        >
          CGT
        </h2>

        <ArrowRightIcon
          onClick={() => navigate(1)}
          style={{
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        />
      </div>
      <section className="cgt-crud-card">
        <h2>Add CGT</h2>

        <form className="cgt-crud-form" onSubmit={onCreate}>
          <div className="cgt-crud-grid">
            <div className="cgt-crud-field">
              <label>Start Date</label>
              <input
                type="date"
                value={formData.CGT_StartDate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, CGT_StartDate: e.target.value }))
                }
                required
              />
            </div>

            <div className="cgt-crud-field">
              <label>CGT ID</label>
              <input
                type="text"
                value={formData.CGTID}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, CGTID: e.target.value }))
                }
                placeholder="Enter CGT ID"
                required
              />
            </div>

            <div className="cgt-crud-field cgt-crud-wide">
              <label>CGT Description</label>
              <input
                type="text"
                value={formData.Description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, Description: e.target.value }))
                }
                placeholder="Enter CGT Description"
                required
              />
            </div>

            <div className="cgt-crud-field">
              <label>Branch</label>
              <select
                value={formData.BranchId}
                onChange={(e) => setFormData((prev) => ({ ...prev, BranchId: e.target.value }))}
                required
              >
                <option value="">Select Branch</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="cgt-crud-field">
              <label>Center</label>
              <select
                value={formData.CenterId}
                onChange={(e) => setFormData((prev) => ({ ...prev, CenterId: e.target.value }))}
                disabled={!formData.BranchId}
                required
              >
                <option value="">Select Center</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.CenterName}
                  </option>
                ))}
              </select>
            </div>

            <div className="cgt-crud-field">
              <label>Group</label>
              <select
                value={formData.groupId}
                onChange={(e) => setFormData((prev) => ({ ...prev, groupId: e.target.value }))}
                disabled={!formData.CenterId}
                required
              >
                <option value="">Select Group</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.groupname}
                  </option>
                ))}
              </select>
            </div>

            <div className="cgt-crud-field">
              <label>Employee (Optional)</label>
              <select
                value={formData.employeId}
                onChange={(e) => setFormData((prev) => ({ ...prev, employeId: e.target.value }))}
                disabled={!formData.groupId}
              >
                <option value="">Select Employee</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.memberName || "Unknown"}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button type="submit" className="cgt-crud-primary-btn" disabled={!canSubmit || submitting}>
            {submitting ? "Saving..." : "Add CGT"}
          </button>
        </form>
      </section>

      <section className="cgt-crud-card">
        <h3>CGT List</h3>
        <div className="cgt-crud-table-wrap">
          <table className="cgt-crud-table">
            <thead>
              <tr>
                <th>#</th>
                <th>CGT ID</th>
                <th>Description</th>
                <th>Start Date</th>
                <th>Employee ID</th>
                <th>Branch ID</th>
                <th>Center ID</th>
                <th>Group ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9">Loading...</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="9">No records found.</td>
                </tr>
              ) : (
                rows.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{index + 1}</td>
                    <td>{item.CGTID || "-"}</td>
                    <td>{item.Description || "-"}</td>
                    <td>{item.CGT_StartDate ? new Date(item.CGT_StartDate).toLocaleDateString("en-GB") : "-"}</td>
                    <td>{item.employeId || "-"}</td>
                    <td>{item.BranchId || "-"}</td>
                    <td>{item.CenterId || "-"}</td>
                    <td>{item.groupId || "-"}</td>
                    <td className="cgt-crud-actions">
                      <button type="button" className="cgt-crud-edit" onClick={() => openEdit(item)}>
                        Update
                      </button>
                      <button type="button" className="cgt-crud-delete" onClick={() => onDelete(item.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {editItem && (
        <div className="cgt-crud-modal-overlay" onClick={closeEdit}>
          <div className="cgt-crud-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Update CGT</h4>
            <form onSubmit={onUpdate} className="cgt-crud-form">
              <div className="cgt-crud-grid">
                <div className="cgt-crud-field cgt-crud-wide">
                  <label>Description</label>
                  <input
                    type="text"
                    value={editData.Description}
                    onChange={(e) =>
                      setEditData((prev) => ({ ...prev, Description: e.target.value }))
                    }
                    required
                  />
                </div>

                <div className="cgt-crud-field">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={editData.CGT_StartDate}
                    onChange={(e) =>
                      setEditData((prev) => ({ ...prev, CGT_StartDate: e.target.value }))
                    }
                    required
                  />
                </div>

                <div className="cgt-crud-field">
                  <label>Employee ID (Optional)</label>
                  <input
                    type="number"
                    value={editData.employeId}
                    onChange={(e) =>
                      setEditData((prev) => ({ ...prev, employeId: e.target.value }))
                    }
                    placeholder="Employee ID"
                  />
                </div>
              </div>

              <div className="cgt-crud-modal-actions">
                <button type="button" className="cgt-crud-secondary-btn" onClick={closeEdit}>
                  Cancel
                </button>
                <button type="submit" className="cgt-crud-primary-btn">
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddCgt;
